import React, { useState, useRef } from 'react';
import { FileText, Upload, FolderPlus, Search, Download, Share2, Trash2, Loader2, Mail } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { toast } from 'react-hot-toast';
import { fileService } from '../../../lib/services/fileService';

interface Document {
  id: string;
  name: string;
  type: string;
  size: string;
  uploadedBy: string;
  uploadedAt: string;
  shared: boolean;
  url?: string;
}

interface DocumentCenterProps {
  contractId: string;
}

export function DocumentCenter({ contractId }: DocumentCenterProps) {
  const [documents, setDocuments] = useState<Document[]>([
    {
      id: '1',
      name: 'Technical Proposal Draft.docx',
      type: 'docx',
      size: '2.4 MB',
      uploadedBy: 'John Doe',
      uploadedAt: '2024-03-10T14:30:00Z',
      shared: true,
    },
    {
      id: '2',
      name: 'Cost Estimation Sheet.xlsx',
      type: 'xlsx',
      size: '1.8 MB',
      uploadedBy: 'Sarah Johnson',
      uploadedAt: '2024-03-09T16:45:00Z',
      shared: false,
    },
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [emailRecipient, setEmailRecipient] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleNewFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      // API call would go here to create new folder
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success(`Folder "${newFolderName}" created successfully`);
      setShowNewFolderModal(false);
      setNewFolderName('');
    } catch (error) {
      toast.error('Failed to create folder');
      console.error('Failed to create folder:', error);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    try {
      // API call would go here to upload files
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Simulate adding new documents
      const newDocs: Document[] = Array.from(files).map(file => ({
        id: crypto.randomUUID(),
        name: file.name,
        type: file.name.split('.').pop() || '',
        size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        uploadedBy: 'You',
        uploadedAt: new Date().toISOString(),
        shared: false,
      }));

      setDocuments(prev => [...prev, ...newDocs]);
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload files');
      console.error('Failed to upload files:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (document: Document) => {
    setDownloading(document.id);
    try {
      await fileService.simulateFileDownload(document.name);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file');
    } finally {
      setDownloading(null);
    }
  };

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDocument || !emailRecipient) return;

    try {
      // API call would go here to share the document
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success(`Shared ${selectedDocument.name} with ${emailRecipient}`);
      setShowShareModal(false);
      setEmailRecipient('');
      setSelectedDocument(null);
    } catch (error) {
      toast.error('Failed to share document');
      console.error('Failed to share document:', error);
    }
  };

  const handleDelete = async (documentId: string) => {
    try {
      // API call would go here to delete the document
      await new Promise(resolve => setTimeout(resolve, 1000));
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      toast.success('Document deleted successfully');
    } catch (error) {
      toast.error('Failed to delete document');
      console.error('Failed to delete document:', error);
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div className="ml-4 flex space-x-3">
          <button
            onClick={() => setShowNewFolderModal(true)}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <FolderPlus className="mr-2 h-4 w-4" />
            New Folder
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            multiple
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload Files
              </>
            )}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {filteredDocuments.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4"
          >
            <div className="flex items-center space-x-4">
              <FileText className="h-8 w-8 text-gray-400" />
              <div>
                <h4 className="font-medium text-gray-900">{doc.name}</h4>
                <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                  <span>{doc.size}</span>
                  <span>•</span>
                  <span>Uploaded by {doc.uploadedBy}</span>
                  <span>•</span>
                  <span>{new Date(doc.uploadedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => handleDownload(doc)}
                disabled={downloading === doc.id}
                className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 disabled:opacity-50"
              >
                {downloading === doc.id ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Download className="h-5 w-5" />
                )}
              </button>
              <button 
                onClick={() => {
                  setSelectedDocument(doc);
                  setShowShareModal(true);
                }}
                className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
              >
                <Share2 className="h-5 w-5" />
              </button>
              <button 
                onClick={() => handleDelete(doc.id)}
                className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* New Folder Modal */}
      <Modal
        isOpen={showNewFolderModal}
        onClose={() => setShowNewFolderModal(false)}
        title="Create New Folder"
      >
        <form onSubmit={handleNewFolder} className="space-y-4">
          <div>
            <label htmlFor="folderName" className="block text-sm font-medium text-gray-700">
              Folder Name
            </label>
            <input
              type="text"
              id="folderName"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="Enter folder name"
              required
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowNewFolderModal(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Create Folder
            </button>
          </div>
        </form>
      </Modal>

      {/* Share Modal */}
      <Modal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setSelectedDocument(null);
          setEmailRecipient('');
        }}
        title="Share Document"
      >
        <form onSubmit={handleShare} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Recipient Email
            </label>
            <input
              type="email"
              id="email"
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="Enter recipient's email"
              required
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowShareModal(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Share
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}